import React, { useState, useEffect } from 'react';
import {
  Box,
  Alert,
  Typography,
  Grid,
  Stack,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TagIcon from '@mui/icons-material/Tag';
import { useAuth } from '../../contexts/AuthContext';
import modelsService from '../../services/modelsService';
import { getUserProfile } from '../../firebase/services/userService';
import BaseDialog from './BaseDialog';
import BaseTextField from './BaseTextField';
import BaseButton from './BaseButton';

/**
 * Dialog pour consulter un modèle de prompt en lecture seule
 * @param {boolean} open - État d'ouverture du dialog
 * @param {function} onClose - Callback de fermeture
 * @param {string} modelId - ID du modèle à afficher
 */
const ViewModelDialog = ({ open, onClose, modelId }) => {
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creatorInfo, setCreatorInfo] = useState(null);
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Charger le modèle quand le dialog s'ouvre
  useEffect(() => {
    if (open && modelId) {
      loadModel();
    }
    // Réinitialiser quand le dialog se ferme
    if (!open) {
      setModel(null);
      setError(null);
      setCreatorInfo(null);
    }
  }, [open, modelId]);

  const loadModel = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const modelData = await modelsService.getModel(modelId);
      
      if (modelData) {
        setModel(modelData);
        
        // Récupérer les informations du créateur si disponible
        const userIdToSearch = modelData.createdBy || modelData.userId;

        if (userIdToSearch) {
          try {
            const userProfile = await getUserProfile(userIdToSearch);

            if (userProfile) {
              setCreatorInfo(userProfile);
            } else {
              console.log('[ViewModelDialog] Aucun profil utilisateur trouvé, utilisation du fallback');
            }
          } catch (error) {
            console.log('[ViewModelDialog] Impossible de récupérer les informations du créateur, utilisation du fallback:', error.message);
          }
        } else {
          console.log('[ViewModelDialog] Aucun ID utilisateur trouvé dans le modèle, utilisation du fallback');
        }
      } else {
        setError('Modèle non trouvé');
      }
    } catch (error) {
      console.error('[ViewModelDialog] Erreur lors du chargement du modèle:', error);
      setError('Erreur lors du chargement du modèle : ' + (error.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date inconnue';
    try {
      // Gestion des timestamps Firebase
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Date inconnue';
    }
  };

  const getCreatorDisplayName = (model) => {

    // Vérifier si c'est l'utilisateur actuel
    if (model.createdBy === currentUser?.uid || model.userId === currentUser?.uid) {
      return 'Vous';
    }
    
    // Si on a récupéré les informations du créateur, les utiliser
    if (creatorInfo) {

      // Priorité au displayName, sinon email, sinon nom d'utilisateur depuis l'email
      if (creatorInfo.displayName) {
        return creatorInfo.displayName;
      }
      if (creatorInfo.email) {
        return creatorInfo.email.split('@')[0];
      }
      if (creatorInfo.name) {
        return creatorInfo.name;
      }
      if (creatorInfo.givenName || creatorInfo.surname) {
        const firstName = creatorInfo.givenName || '';
        const lastName = creatorInfo.surname || '';
        return `${firstName} ${lastName}`.trim();
      }

      // Si on a un creatorInfo mais aucun champ utilisable
      console.log('[ViewModelDialog] creatorInfo existe mais aucun champ nom utilisable, utilisation du fallback');
    } else {
      console.log('[ViewModelDialog] Pas de creatorInfo disponible, utilisation du fallback');
    }
    
    // Logique de fallback améliorée
    if (model.createdBy === 'Admin') {
      return 'Administrateur';
    } else if (model.createdBy || model.userId) {
      const uid = model.createdBy || model.userId;
      
      // Si c'est un utilisateur Azure, essayer d'extraire des informations du nom
      if (uid?.startsWith('azure-')) {
        // Extraire l'ID après "azure-" et afficher de manière plus lisible
        const azureId = uid.substring(6); // Enlever "azure-"
        if (azureId.length > 8) {
          return `Utilisateur Azure (${azureId.substring(0, 8)}...)`;
        }
        return `Utilisateur Azure (${azureId})`;
      }
      
      // Pour les autres UIDs, afficher les 8 premiers caractères
      return `Utilisateur (${uid.substring(0, 8)}...)`;
    } else {
      return 'Utilisateur inconnu';
    }
  };

  const handleClose = () => {
    setModel(null);
    setError(null);
    onClose();
  };

  // Actions du dialog
  const dialogActions = (
    <BaseButton 
      onClick={handleClose} 
      variant="contained"
      sx={{ 
        px: 4,
        py: 1
      }}
    >
      Fermer
    </BaseButton>
  );

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title="👁️ Consulter le Prompt"
      subtitle="Aperçu détaillé du modèle de prompt"
      maxWidth="md"
      actions={dialogActions}
      customBackgroundContent="#fafbff"
    >
      {loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Chargement du modèle...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {model && (
        <Box 
          sx={{ 
            background: 'linear-gradient(145deg, rgba(255,255,255,0.8) 0%, rgba(248,250,255,0.9) 100%)',
            borderRadius: '20px',
            p: 3,
            border: '1px solid rgba(255,255,255,0.3)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Grid container spacing={3}>
            {/* Titre */}
            <Grid item xs={12}>
              <BaseTextField
                label="🔖 Titre du prompt"
                value={model.title}
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
                size="small"
                sx={{
                  '& .MuiInputBase-input': {
                    fontWeight: 600,
                    color: '#3F51B5'
                  }
                }}
              />
            </Grid>

            {/* Description */}
            {model.description && (
              <Grid item xs={12}>
                <BaseTextField
                  label="📝 Description"
                  value={model.description}
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={4}
                  InputProps={{
                    readOnly: true,
                  }}
                  size="small"
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      color: '#2c3e50'
                    }
                  }}
                />
              </Grid>
            )}

            {/* Informations de création */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                background: 'rgba(255,255,255,0.6)',
                borderRadius: '16px',
                p: 3,
                border: '1px solid rgba(25, 118, 210, 0.1)',
                backdropFilter: 'blur(8px)',
                height: '100%'
              }}>
                <Typography 
                  variant="subtitle2" 
                  gutterBottom
                  sx={{ 
                    mb: 3,
                    fontWeight: 600,
                    color: 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  ℹ️ Informations
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PersonIcon fontSize="small" sx={{ color: '#42a5f5' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Créé par
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                        {getCreatorDisplayName(model)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CalendarTodayIcon fontSize="small" sx={{ color: '#42a5f5' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Créé le
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                        {formatDate(model.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                  {model.updatedAt && model.updatedAt !== model.createdAt && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CalendarTodayIcon fontSize="small" sx={{ color: '#42a5f5' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Modifié le
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                          {formatDate(model.updatedAt)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Tags */}
            {model.tags && model.tags.length > 0 && (
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  background: 'rgba(255,255,255,0.6)',
                  borderRadius: '16px',
                  p: 3,
                  border: '1px solid rgba(25, 118, 210, 0.1)',
                  backdropFilter: 'blur(8px)',
                  height: '100%'
                }}>
                  <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ 
                      mb: 3,
                      fontWeight: 600,
                      color: 'text.primary',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <TagIcon fontSize="small" /> Tags ({model.tags.length})
                  </Typography>
                  <Stack 
                    direction="row" 
                    spacing={1} 
                    flexWrap="wrap"
                    sx={{ gap: 1 }}
                  >
                    {model.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: '8px',
                          borderColor: '#3F51B5',
                          color: '#3F51B5',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          background: 'rgba(63, 81, 181, 0.05)',
                          '&:hover': {
                            background: 'rgba(63, 81, 181, 0.1)',
                          }
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Grid>
            )}

            {/* Contenu du prompt */}
            <Grid item xs={12}>
              <BaseTextField
                name="prompt"
                label="🤖 Contenu du prompt"
                fullWidth
                multiline
                minRows={6}
                maxRows={12}
                value={model.prompt || ''}
                InputProps={{
                  readOnly: true,
                }}
                size="small"
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                    lineHeight: 1.8,
                    color: '#2c3e50',
                    fontFamily: 'Fira Code, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                  }
                }}
              />
            </Grid>

            {/* Paramètres IA (si disponibles) */}
            {(model.temperature !== undefined || model.maxTokens !== undefined) && (
              <Grid item xs={12}>
                <Box sx={{ 
                  background: 'rgba(255,255,255,0.6)',
                  borderRadius: '16px',
                  p: 3,
                  border: '1px solid rgba(25, 118, 210, 0.1)',
                  backdropFilter: 'blur(8px)'
                }}>
                  <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ 
                      mb: 3,
                      fontWeight: 600,
                      color: 'text.primary',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    🎛️ Paramètres IA
                  </Typography>
                  <Grid container spacing={2}>
                    {model.temperature !== undefined && (
                      <Grid item xs={12} sm={6}>
                        <BaseTextField
                          label="🌡️ Température"
                          value={model.temperature}
                          fullWidth
                          InputProps={{
                            readOnly: true,
                          }}
                          size="small"
                        />
                      </Grid>
                    )}
                    {model.maxTokens !== undefined && (
                      <Grid item xs={12} sm={6}>
                        <BaseTextField
                          label="📊 Tokens max"
                          value={model.maxTokens}
                          fullWidth
                          InputProps={{
                            readOnly: true,
                          }}
                          size="small"
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </BaseDialog>
  );
};

export default ViewModelDialog; 